import { useState } from "react";
import { Sparkles } from "lucide-react";
import { useSearchParams } from "react-router-dom";

/**
 * Shared banner shown across Module 1, Module 2 and Module 3 when a demo
 * profile is currently applied to the assessment flow.
 *
 * The "applied" state lives in two places (kept in sync by the dashboard
 * "Try Demo" entry-point):
 *   - URL query string: `?demo={profile_name}` (deep-linkable)
 *   - localStorage["warehousex_demo_applied"] = profile_name (survives navigation)
 *
 * If neither is present, the banner renders nothing — so it stays invisible
 * for normal client/public users who never went through the demo entry-point.
 *
 * Visibility is therefore implicitly gated by who *can* set those values
 * (admin / demo seed flow). No role check is needed in this component.
 */
export function DemoProfileBanner({ stepNumber }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedProfileName =
    searchParams.get("demo") ||
    (typeof window !== "undefined"
      ? localStorage.getItem("warehousex_demo_applied")
      : "") ||
    "";

  const [dismissed, setDismissed] = useState(false);

  if (!appliedProfileName || dismissed) return null;

  let label = appliedProfileName;
  try {
    label = decodeURIComponent(appliedProfileName);
  } catch {
    /* fall through to raw value */
  }

  return (
    <div
      data-testid={stepNumber ? `module${stepNumber}-demo-banner` : "demo-banner"}
      className="mb-5 flex items-start justify-between gap-3 rounded-sm border border-[#DDD6FE] bg-[#F0EDF9] px-4 py-3"
    >
      <div className="flex items-start gap-2 min-w-0">
        <Sparkles className="w-4 h-4 text-[#5847B0] mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[#4C1D95]">
            Demo profile applied: {label}
          </p>
          <p className="text-[11px] text-[#5847B0]/80">
            All three steps are prefilled. You can continue or edit any answer.
          </p>
        </div>
      </div>
      <button
        type="button"
        className="text-[11px] text-[#5847B0] hover:underline shrink-0"
        onClick={() => {
          setDismissed(true);
          if (searchParams.get("demo")) {
            searchParams.delete("demo");
            setSearchParams(searchParams, { replace: true });
          }
          try {
            localStorage.removeItem("warehousex_demo_applied");
          } catch {
            /* non-blocking */
          }
        }}
      >
        Dismiss
      </button>
    </div>
  );
}

export default DemoProfileBanner;
